
import emailjs from '@emailjs/browser';
import { SubmissionLog, ClientProfile } from '../types';

/**
 * EMAIL SERVICE
 * 
 * Uses EmailJS to send emails from the client side.
 */

export const getEmailConfig = () => {
    const meta = import.meta as any;
    const env = meta.env || {};
    
    const serviceId = env.VITE_EMAILJS_SERVICE_ID || '';
    const defaultTemplateId = env.VITE_EMAILJS_TEMPLATE_ID || '';
    const welcomeTemplateId = (env.VITE_EMAILJS_WELCOME_TEMPLATE_ID && env.VITE_EMAILJS_WELCOME_TEMPLATE_ID !== 'welcome_template_id') 
        ? env.VITE_EMAILJS_WELCOME_TEMPLATE_ID 
        : '';
    const publicKey = env.VITE_EMAILJS_PUBLIC_KEY || '';

    return {
        serviceId,
        defaultTemplateId,
        welcomeTemplateId,
        publicKey,
    };
};

interface EmailResult {
    success: boolean;
    method: 'emailjs' | 'mock';
    error?: any;
}

export const sendEmail = async (options: { to: string, subject: string, body: string, data?: Record<string, any>, templateIdOverride?: string }): Promise<EmailResult> => {
    const { to, subject, body, data, templateIdOverride } = options;
    const config = getEmailConfig();

    console.log(`%c[EMAIL] Preparing to send to: ${to}`, 'color: #2ECC40; font-weight: bold;');

    try {
        const templateParams = {
            to_email: to,
            email: to,
            to: to,
            subject: subject,
            message: body,
            ...data 
        };

        const templateId = templateIdOverride || config.defaultTemplateId;

        // Try server-side proxy first (Secure)
        try {
            const response = await fetch('/api/email/send', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    templateId,
                    templateParams
                })
            });

            if (response.ok) {
                console.log(`%c[EMAIL SUCCESS] Sent via Server Proxy to ${to}`, 'color: green; font-weight: bold;');
                return { success: true, method: 'emailjs' };
            }
            
            const errorData = await response.json();
            console.warn("[EMAIL PROXY] Server returned error, falling back to client-side:", errorData.error);
        } catch (proxyError) {
            console.warn("[EMAIL PROXY] Connection failed, falling back to client-side.");
        }

        // --- FALLBACK TO CLIENT-SIDE (Legacy/Demo) ---
        if (!config.publicKey || !config.serviceId) {
            throw new Error("No email configuration found on client or server.");
        }

        const response = await emailjs.send(
            config.serviceId,
            templateId,
            templateParams,
            config.publicKey
        );

        if (response.status === 200) {
            console.log(`%c[EMAIL SUCCESS] Sent via Client to ${to}`, 'color: green; font-weight: bold;');
            return { success: true, method: 'emailjs' };
        } else {
            throw new Error(`EmailJS Error: ${response.text || 'Unknown Error'}`);
        }

    } catch (error: any) {
        console.error('[EMAIL ERROR] Delivery failed:', error);
        return { 
            success: false, 
            method: 'emailjs', 
            error: error?.text || error?.message || String(error) 
        }; 
    }
};

// --- Standard Email Functions ---

export const sendWelcomeEmail = (to: string, clientProfile: ClientProfile) => {
    const config = getEmailConfig();
    const firstName = clientProfile.userData.name.split(' ')[0] || "there";
    const eligiblePrograms = clientProfile.eligiblePrograms.map(p => `- ${p.name}: ${p.status === 'action-required' || p.status === 'started' ? 'Eligible' : 'In Progress'}`).join('\n');

    return sendEmail({ 
        to, 
        subject: `Welcome to ForgivePath, ${firstName}! Your Results Are Here`, 
        body: `Hi ${firstName},\n\nWelcome to ForgivePath! Your account is ready.\n\nBased on your eligibility test, here's a summary of programs you may qualify for:\n\n${eligiblePrograms}\n\nNext Step: Complete your payment to unlock full application prep and detailed guidance.\n\nLogin to your dashboard at ${window.location.origin} to get started.\n\nBest,\nThe ForgivePath Team`,
        data: {
            firstName: firstName,
            eligiblePrograms: eligiblePrograms,
            loginUrl: window.location.origin,
        },
        templateIdOverride: config.welcomeTemplateId // Use the specific welcome template
    });
};

export const sendPaymentEmail = (to: string) => {
    return sendEmail({ 
        to, 
        subject: "Payment Confirmed - ForgivePath", 
        body: `Thank you! Your payment of $79 is confirmed. You now have full access to your dashboard.` 
    });
};

export const sendSubmissionEmail = (to: string, log: SubmissionLog, files: Record<string, File>) => {
    const programSummary = log.programs.map(p => `- ${p.name}: ${p.confirmationId ? 'Submitted' : 'Packet Ready'}`).join('\n');
    return sendEmail({ 
        to, 
        subject: `Submission Confirmation - ForgivePath`, 
        body: `Your application packet is ready!\n\nSummary:\n${programSummary}\n\nLog in to your dashboard for next steps.` 
    });
};

export const sendFeedbackEmail = (chatHistory: { role: 'user' | 'model'; content: string }[], userEmail: string) => {
    const formattedHistory = chatHistory.map(msg => `[${msg.role}]: ${msg.content}`).join('\n\n');
    return sendEmail({ 
        to: "YASEnterpriseLLC@gmail.com", 
        subject: "New Support Request", 
        body: `Feedback from: ${userEmail}\n\n--- LOG ---\n${formattedHistory}` 
    });
};

export const sendTestEmail = async (to: string) => {
    return sendEmail({
        to,
        subject: "🔧 ForgivePath Production Connection Test",
        body: "Success! Your website and EmailJS are correctly talking to each other in production."
    });
};
